import { jwtDecode } from "jwt-decode";

export const USER_ROLES = Object.freeze({
  ADMIN: "ADMIN",
  PHOTOGRAPHER: "PHOTOGRAPHER",
  USER: "USER",
});

export const ROLE_HOME_PATHS = Object.freeze({
  [USER_ROLES.ADMIN]: "/Menu",
  [USER_ROLES.PHOTOGRAPHER]: "/Menu",
  [USER_ROLES.USER]: "/AreaAluno",
});

const ROLE_LABELS = Object.freeze({
  [USER_ROLES.ADMIN]: "Administrador",
  [USER_ROLES.PHOTOGRAPHER]: "Fotógrafo",
  [USER_ROLES.USER]: "Aluno/Responsavel",
});

export const isUserRole = (role) =>
  Object.values(USER_ROLES).includes(role);

export const SESSION_STATUS = {
  LOADING: "loading",
  AUTHENTICATED: "authenticated",
  LOST: "lost",
};

export const STUDENT_ACCESS_TYPES = Object.freeze({
  STUDENT: "STUDENT",
  RESPONSIBLE: "RESPONSIBLE",
});

const AUTH_TOKEN_STORAGE_KEY = "formari.auth.token";
const REFRESH_TOKEN_STORAGE_KEY = "formari.auth.refreshToken";

export const getStoredToken = () => {
  try {
    return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

export const persistToken = (token) => {
  try {
    if (!token) {
      window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
  } catch {
    // Local storage indisponivel nao deve quebrar a aplicacao.
  }
};

export const getStoredRefreshToken = () => {
  try {
    return window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

export const persistRefreshToken = (refreshToken) => {
  try {
    if (!refreshToken) {
      window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
  } catch {
    // Local storage indisponivel nao deve quebrar a aplicacao.
  }
};

export const clearStoredToken = () => {
  persistToken("");
  persistRefreshToken("");
};

export const decodeTokenSafely = (token) => {
  if (!token) return null;

  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

export const bootstrapStoredSession = () => {
  const token = getStoredToken();

  if (!token) {
    clearStoredToken();
    return {
      token: "",
      user: null,
      loggedIn: false,
      status: SESSION_STATUS.LOST,
    };
  }

  return {
    token,
    user: null,
    loggedIn: false,
    status: SESSION_STATUS.LOADING,
  };
};

export const normalizeStudentAccesses = (studentAccesses) => {
  if (!Array.isArray(studentAccesses)) return [];

  const uniqueAccesses = new Map();

  studentAccesses.forEach((access) => {
    if (
      !access ||
      typeof access.studentId !== "string" ||
      !access.studentId.trim() ||
      typeof access.personId !== "string" ||
      !access.personId.trim() ||
      typeof access.classId !== "string" ||
      !access.classId.trim() ||
      !Object.values(STUDENT_ACCESS_TYPES).includes(access.accessType)
    ) {
      return;
    }

    const studentId = access.studentId.trim();
    if (uniqueAccesses.has(studentId)) return;

    uniqueAccesses.set(studentId, {
      studentId,
      personId: access.personId.trim(),
      classId: access.classId.trim(),
      accessType: access.accessType,
    });
  });

  return Array.from(uniqueAccesses.values());
};

export const getStudentAccesses = (user) =>
  user?.role === USER_ROLES.USER
    ? normalizeStudentAccesses(user.studentAccesses)
    : [];

export const reconcileActiveStudentId = (studentAccesses, activeStudentId) => {
  const accesses = normalizeStudentAccesses(studentAccesses);
  const currentAccess = accesses.find(
    (access) => access.studentId === activeStudentId,
  );

  return currentAccess?.studentId || accesses[0]?.studentId || "";
};

export const normalizeAuthenticatedUser = (user) => {
  if (
    !user ||
    !isUserRole(user.role) ||
    user.status !== true ||
    typeof user.companyId !== "string" ||
    !user.companyId.trim()
  ) {
    return null;
  }

  const normalizedUser = { ...user };

  if (normalizedUser.role === USER_ROLES.USER) {
    normalizedUser.studentAccesses = normalizeStudentAccesses(
      normalizedUser.studentAccesses,
    );
  } else {
    delete normalizedUser.studentAccesses;
  }

  return normalizedUser;
};

export const hasAnyRole = (user, allowedRoles = []) => {
  if (!isUserRole(user?.role)) return false;
  if (!allowedRoles.length) return true;
  return allowedRoles.includes(user.role);
};

export const getRoleLabel = (role) => ROLE_LABELS[role] || null;

export const getDashboardPath = (role) => ROLE_HOME_PATHS[role] || null;
